import type { RecentlyUpdatedMacroQuery$variables } from '../__generated__/RecentlyUpdatedMacroQuery.graphql.ts';

import type { RecentlyUpdatedMacroParams } from './recently-updated-types.ts';

export const DEFAULT_MAX = 15;

export const DEFAULT_TYPES = [
	'page',
	'blogpost',
	'comment',
	'attachment',
	'whiteboard',
	'database',
	'embed',
	'folder',
];

export function resolveMaxItems(max: RecentlyUpdatedMacroParams['max']): number {
	const parsed = Number(max);
	return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX;
}

/**
 * Builds a CQL query string from the macro params, matching the behaviour of the
 * legacy RecentUpdateQueryParameters Java class which accepts:
 *   - followingUsers  (author param — comma-separated account IDs)
 *   - labels          (comma-separated label values)
 *   - spaceKeys       (comma-separated space keys)
 *   - contentTypes    (comma-separated types: page, blogpost, comment, attachment)
 *
 * @param params - Resolved macro parameters with string values.
 * @param spaceKey - The effective space key (already resolved from params.spaces,
 *   params.currentSpaceKey, or page context by the caller).
 */
export function buildCql(params: RecentlyUpdatedMacroParams, spaceKey: string): string {
	const clauses: string[] = [];

	// Content types
	const rawTypes: string[] = [];
	if (params.types) {
		const splitTypes = String(params.types).split(',');
		for (const t of splitTypes) {
			const trimmed = t.trim();
			if (trimmed) rawTypes.push(trimmed);
		}
	} else {
		rawTypes.push(...DEFAULT_TYPES);
	}
	if (rawTypes.length > 0) {
		clauses.push(`type in (${rawTypes.join(', ')})`);
	}

	// Space key — defaults to current space
	if (spaceKey) {
		clauses.push(`space = "${spaceKey}"`);
	}

	// Labels
	if (params.labels) {
		const labelList = String(params.labels)
			.split(',')
			.map((l) => `"${l.trim()}"`)
			.join(', ');
		clauses.push(`label in (${labelList})`);
	}

	// Following users / contributors (author param — comma-separated account IDs)
	if (params.author) {
		const users = String(params.author)
			.split(',')
			.map((u) => `"${u.trim()}"`)
			.join(', ');
		clauses.push(`contributor in (${users})`);
	}

	return `${clauses.join(' AND ')} order by lastmodified desc`;
}

export function buildRecentlyUpdatedQueryVariables(
	params: RecentlyUpdatedMacroParams,
	spaceKey: string,
	after: string = '',
): RecentlyUpdatedMacroQuery$variables {
	const maxItems = resolveMaxItems(params.max);

	return {
		cql: buildCql(params, spaceKey),
		cqlcontext: '{"contentStatuses":["draft", "current"]}',
		first: maxItems * 2,
		after,
	};
}
